import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './schedulingPermits.css';

function SchedulingPermits({ booking, onClose, onBookingUpdated }) {
    const [permits, setPermits] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [files, setFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [error, setError] = useState('');

    const fileInputRef = useRef(null);

    // Load all permits for this booking
    useEffect(() => {
        if (!booking) return;

        const fetchPermits = async () => {
            setIsLoading(true);
            setError('');
            try {
                const res = await axios.get(`/api/scheduling/permits/${booking.id}`);
                setPermits(res.data.permits || []);
            } catch (err) {
                console.error('Failed to load permits:', err);
                setError('Failed to load permits. Please try again.');
            } finally {
                setIsLoading(false);
            }
        };

        fetchPermits();
    }, [booking]);

    const validateAndCollectFiles = (incoming) => {
        const allowed = [];
        let hasInvalid = false;

        incoming.forEach((f) => {
            const isImage = f.type && f.type.startsWith('image/');
            if (isImage) {
                allowed.push(f);
            } else {
                hasInvalid = true;
            }
        });

        if (hasInvalid) {
            setError('Only image files are allowed (e.g. JPG, PNG).');
        } else {
            setError('');
        }

        if (!allowed.length) return;
        setFiles((prev) => [...prev, ...allowed]);
    };

    const handleFileChange = (e) => {
        const selected = Array.from(e.target.files || []);
        validateAndCollectFiles(selected);
        e.target.value = '';
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const droppedFiles = Array.from(e.dataTransfer.files || []);
        validateAndCollectFiles(droppedFiles);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!files.length) {
            setError('Please select at least one file to upload.');
            return;
        }

        setIsUploading(true);
        setError('');

        try {
            // Upload each file as a separate permit
            for (const file of files) {
                const formData = new FormData();
                formData.append('file', file);

                await axios.post(
                    `/api/scheduling/permits/${booking.id}`,
                    formData,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                    }
                );
            }

            // Refresh permits after upload
            const res = await axios.get(`/api/scheduling/permits/${booking.id}`);
            const updatedPermits = res.data.permits || [];
            setPermits(updatedPermits);
            setFiles([]);

            // Update booking flags (has_permit + last permit_file_url)
            const latest = updatedPermits[updatedPermits.length - 1];
            if (onBookingUpdated) {
                onBookingUpdated({
                    has_permit: updatedPermits.length > 0,
                    permit_file_url: latest?.file_url || null,
                });
            }
        } catch (err) {
            console.error('Failed to upload permits:', err);
            setError('Failed to upload permits. Please try again.');
        } finally {
            setIsUploading(false);
        }
    };

    const handleDelete = async (permitId) => {
        const confirmed = window.confirm('Are you sure you want to remove this permit? This cannot be undone.');
        if (!confirmed) return;

        try {
            await axios.delete(`/api/scheduling/permits/${permitId}`);

            setPermits((prev) => {
                const next = prev.filter((p) => p.id !== permitId);
                const latest = next[next.length - 1];

                if (onBookingUpdated) {
                    onBookingUpdated({
                        has_permit: next.length > 0,
                        permit_file_url: latest?.file_url || null,
                    });
                }

                return next;
            });
        } catch (err) {
            console.error('Failed to delete permit:', err);
            setError('Failed to delete permit. Please try again.');
        }
    };

    return (
        <div className="scheduling-modal-overlay">
            <div className="scheduling-modal">
                {/* Close X */}
                <button
                    type="button"
                    className="modal-x"
                    onClick={onClose}
                >
                    &times;
                </button>

                <div className="scheduling-modal-header">
                    <h3>Manage permit</h3>
                </div>

                <div className="scheduling-modal-body">
                    <p className="scheduling-modal-booking-line">
                        Booking #{booking.id} · Unit {booking.unit_number} –{' '}
                        {booking.building_name}
                    </p>

                    {isLoading ? (
                        <p>Loading permits...</p>
                    ) : (
                        <>
                            {permits.length === 0 ? (
                                <p>No permits uploaded yet.</p>
                            ) : (
                                <div className="scheduling-permit-grid">
                                    {permits.map((p) => (
                                        <div
                                            key={p.id}
                                            className="scheduling-permit-thumb-wrapper"
                                        >
                                            <a
                                                href={p.file_url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="scheduling-permit-thumb-link"
                                            >
                                                <div className="scheduling-permit-thumb">
                                                    <img
                                                        src={p.file_url}
                                                        alt="Permit"
                                                        onError={(e) => {
                                                            // Fallback if URL is broken
                                                            e.target.style.opacity = 0.4;
                                                            e.target.alt = 'Broken image';
                                                        }}
                                                    />
                                                </div>
                                            </a>
                                            <button
                                                type="button"
                                                className="scheduling-permit-delete-button"
                                                onClick={() => handleDelete(p.id)}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}

                    {/* UPLOAD SECTION – drop-zone / file-list styles reused */}
                    <form
                        onSubmit={handleSubmit}
                        className="scheduling-permit-upload-form"
                    >
                        <label className="scheduling-label">
                            Upload new permits (images only)
                        </label>

                        <div
                            className="drop-zone"
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <p>
                                Drag &amp; drop permit images here or click to
                                browse
                            </p>
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                accept="image/*"
                                onChange={handleFileChange}
                                className="file-input"
                                style={{ display: 'none' }}
                            />
                        </div>

                        {files.length > 0 && (
                            <ul className="file-list">
                                {files.map((f, i) => (
                                    <li key={`${f.name}-${i}`}>{f.name}</li>
                                ))}
                            </ul>
                        )}

                        {error && (
                            <div className="scheduling-error-text">{error}</div>
                        )}

                        <button
                            type="submit"
                            className="scheduling-primary-button"
                            disabled={isUploading || !files.length}
                        >
                            {isUploading ? 'Uploading...' : 'Upload Permit'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default SchedulingPermits;